/**
 * Historical annual returns 1928-2025 for the Trinity Study back-test.
 *
 * Four series:
 *   - S&P 500 nominal annual return (with dividends).
 *   - 10-year US Treasury nominal annual return (price + coupon).
 *   - US CPI-U annual change (inflation rate).
 *   - Shiller CAPE (PE10) annual closing value — Phase 6 Session 3 add,
 *     consumed by the CAPE-based withdrawal strategy.
 *
 * Plus a derived real-return series (CPI-adjusted) used by the Trinity /
 * sequence-of-returns helpers, and a CAPE-augmented real-return view for
 * the CAPE-based strategy.
 *
 * Source: Aswath Damodaran (NYU Stern), "Historical Returns: Stocks, T.Bonds
 * & T.Bills with premiums" — pages.stern.nyu.edu/~adamodar/. CPI series from
 * BLS CPI-U All Urban Consumers, annual averages, via usinflationcalculator.com.
 * CAPE series from multpl.com/shiller-pe/table/by-year (Robert Shiller-derived
 * mirror of `ie_data.xls` at econ.yale.edu/~shiller/data/). All three sources
 * retrieved 2026-05-08.
 *
 * Real-return formulation (Fisher equation):
 *   real = ((1 + nominal) / (1 + cpi) - 1) × 100
 *
 * Why real, not nominal+CPI? Trinity Study consumers want "withdraw a constant
 * lifestyle in today's dollars." Real returns + constant nominal withdrawal is
 * mathematically equivalent to nominal returns + inflation-adjusted withdrawal,
 * but ships cleaner and avoids accumulated rounding in two parallel index walks.
 *
 * Spot-checks pass against published Damodaran real-return columns:
 *   1933: +58.04%   (canonical post-bottom rally)
 *   1973: -19.32%   (stagflation start)
 *   1974: -33.24%   (worst single year of the 70s bear)
 *   2008: -38.87%   (GFC trough)
 *   2022: -24.11%   (concurrent inflation + rate spike)
 *
 * CAPE spot-checks (multpl.com mirror):
 *   1929: 27.08     (pre-Crash peak year)
 *   1932: 9.31      (Depression trough)
 *   2000: 43.77     (dot-com peak — highest in series)
 *   2008: 24.02     (GFC year-anchor)
 *   2022: 36.94     (post-COVID rate-spike peak)
 */

export interface HistoricalYear {
  /** Calendar year, e.g. 1928. */
  readonly year: number;
  /** S&P 500 nominal total return % (with dividends). */
  readonly equity_nominal_pct: number;
  /** 10-year US Treasury nominal total return %. */
  readonly bond_nominal_pct: number;
  /** US CPI-U annual % change. Positive = inflation, negative = deflation. */
  readonly cpi_pct: number;
  /** Shiller CAPE / PE10 annual value (Phase 6 Session 3 additive). */
  readonly cape_value: number;
}

export interface HistoricalRealYear {
  readonly year: number;
  /** S&P 500 real return % (CPI-adjusted via Fisher). */
  readonly equity_real_pct: number;
  /** 10-year US Treasury real return % (CPI-adjusted via Fisher). */
  readonly bond_real_pct: number;
}

export interface HistoricalRealYearWithCape extends HistoricalRealYear {
  readonly cape_value: number;
}

export const HISTORICAL_RETURNS: readonly HistoricalYear[] = [
  { year: 1928, equity_nominal_pct: 43.81, bond_nominal_pct: 0.84, cpi_pct: -1.7, cape_value: 18.81 },
  { year: 1929, equity_nominal_pct: -8.30, bond_nominal_pct: 4.20, cpi_pct: 0.0, cape_value: 27.08 },
  { year: 1930, equity_nominal_pct: -25.12, bond_nominal_pct: 4.54, cpi_pct: -2.3, cape_value: 22.31 },
  { year: 1931, equity_nominal_pct: -43.84, bond_nominal_pct: -2.56, cpi_pct: -9.0, cape_value: 16.71 },
  { year: 1932, equity_nominal_pct: -8.64, bond_nominal_pct: 8.79, cpi_pct: -9.9, cape_value: 9.31 },
  { year: 1933, equity_nominal_pct: 49.98, bond_nominal_pct: 1.86, cpi_pct: -5.1, cape_value: 8.73 },
  { year: 1934, equity_nominal_pct: -1.19, bond_nominal_pct: 7.96, cpi_pct: 3.1, cape_value: 13.03 },
  { year: 1935, equity_nominal_pct: 46.74, bond_nominal_pct: 4.47, cpi_pct: 2.2, cape_value: 11.50 },
  { year: 1936, equity_nominal_pct: 31.94, bond_nominal_pct: 5.02, cpi_pct: 1.5, cape_value: 17.09 },
  { year: 1937, equity_nominal_pct: -35.34, bond_nominal_pct: 1.38, cpi_pct: 3.6, cape_value: 21.62 },
  { year: 1938, equity_nominal_pct: 29.28, bond_nominal_pct: 4.21, cpi_pct: -2.1, cape_value: 13.51 },
  { year: 1939, equity_nominal_pct: -1.10, bond_nominal_pct: 4.41, cpi_pct: -1.4, cape_value: 15.60 },
  { year: 1940, equity_nominal_pct: -10.67, bond_nominal_pct: 5.40, cpi_pct: 0.7, cape_value: 16.38 },
  { year: 1941, equity_nominal_pct: -12.77, bond_nominal_pct: -2.02, cpi_pct: 5.0, cape_value: 13.90 },
  { year: 1942, equity_nominal_pct: 19.17, bond_nominal_pct: 2.29, cpi_pct: 10.9, cape_value: 10.10 },
  { year: 1943, equity_nominal_pct: 25.06, bond_nominal_pct: 2.49, cpi_pct: 6.1, cape_value: 10.15 },
  { year: 1944, equity_nominal_pct: 19.03, bond_nominal_pct: 2.58, cpi_pct: 1.7, cape_value: 11.05 },
  { year: 1945, equity_nominal_pct: 35.82, bond_nominal_pct: 3.80, cpi_pct: 2.3, cape_value: 11.96 },
  { year: 1946, equity_nominal_pct: -8.43, bond_nominal_pct: 3.13, cpi_pct: 8.3, cape_value: 15.62 },
  { year: 1947, equity_nominal_pct: 5.20, bond_nominal_pct: 0.92, cpi_pct: 14.4, cape_value: 11.47 },
  { year: 1948, equity_nominal_pct: 5.70, bond_nominal_pct: 1.95, cpi_pct: 8.1, cape_value: 10.42 },
  { year: 1949, equity_nominal_pct: 18.30, bond_nominal_pct: 4.66, cpi_pct: -1.2, cape_value: 10.25 },
  { year: 1950, equity_nominal_pct: 30.81, bond_nominal_pct: 0.43, cpi_pct: 1.3, cape_value: 10.75 },
  { year: 1951, equity_nominal_pct: 23.68, bond_nominal_pct: -0.30, cpi_pct: 7.9, cape_value: 11.90 },
  { year: 1952, equity_nominal_pct: 18.15, bond_nominal_pct: 2.27, cpi_pct: 1.9, cape_value: 12.53 },
  { year: 1953, equity_nominal_pct: -1.21, bond_nominal_pct: 4.14, cpi_pct: 0.8, cape_value: 13.01 },
  { year: 1954, equity_nominal_pct: 52.56, bond_nominal_pct: 3.29, cpi_pct: 0.7, cape_value: 12.00 },
  { year: 1955, equity_nominal_pct: 32.60, bond_nominal_pct: -1.34, cpi_pct: -0.4, cape_value: 15.99 },
  { year: 1956, equity_nominal_pct: 7.44, bond_nominal_pct: -2.26, cpi_pct: 1.5, cape_value: 18.29 },
  { year: 1957, equity_nominal_pct: -10.46, bond_nominal_pct: 6.80, cpi_pct: 3.3, cape_value: 16.72 },
  { year: 1958, equity_nominal_pct: 43.72, bond_nominal_pct: -2.10, cpi_pct: 2.8, cape_value: 13.79 },
  { year: 1959, equity_nominal_pct: 12.06, bond_nominal_pct: -2.65, cpi_pct: 0.7, cape_value: 17.98 },
  { year: 1960, equity_nominal_pct: 0.34, bond_nominal_pct: 11.64, cpi_pct: 1.7, cape_value: 18.34 },
  { year: 1961, equity_nominal_pct: 26.64, bond_nominal_pct: 2.06, cpi_pct: 1.0, cape_value: 18.47 },
  { year: 1962, equity_nominal_pct: -8.81, bond_nominal_pct: 5.69, cpi_pct: 1.0, cape_value: 21.20 },
  { year: 1963, equity_nominal_pct: 22.61, bond_nominal_pct: 1.68, cpi_pct: 1.3, cape_value: 19.26 },
  { year: 1964, equity_nominal_pct: 16.42, bond_nominal_pct: 3.73, cpi_pct: 1.3, cape_value: 21.63 },
  { year: 1965, equity_nominal_pct: 12.40, bond_nominal_pct: 0.72, cpi_pct: 1.6, cape_value: 23.27 },
  { year: 1966, equity_nominal_pct: -9.97, bond_nominal_pct: 2.91, cpi_pct: 2.9, cape_value: 24.06 },
  { year: 1967, equity_nominal_pct: 23.80, bond_nominal_pct: -1.58, cpi_pct: 3.1, cape_value: 20.43 },
  { year: 1968, equity_nominal_pct: 10.81, bond_nominal_pct: 3.27, cpi_pct: 4.2, cape_value: 21.51 },
  { year: 1969, equity_nominal_pct: -8.24, bond_nominal_pct: -5.01, cpi_pct: 5.5, cape_value: 21.19 },
  { year: 1970, equity_nominal_pct: 3.56, bond_nominal_pct: 16.75, cpi_pct: 5.7, cape_value: 17.09 },
  { year: 1971, equity_nominal_pct: 14.22, bond_nominal_pct: 9.79, cpi_pct: 4.4, cape_value: 16.46 },
  { year: 1972, equity_nominal_pct: 18.76, bond_nominal_pct: 2.82, cpi_pct: 3.2, cape_value: 17.26 },
  { year: 1973, equity_nominal_pct: -14.31, bond_nominal_pct: 3.66, cpi_pct: 6.2, cape_value: 18.71 },
  { year: 1974, equity_nominal_pct: -25.90, bond_nominal_pct: 1.99, cpi_pct: 11.0, cape_value: 13.53 },
  { year: 1975, equity_nominal_pct: 37.00, bond_nominal_pct: 3.61, cpi_pct: 9.1, cape_value: 8.92 },
  { year: 1976, equity_nominal_pct: 23.83, bond_nominal_pct: 15.98, cpi_pct: 5.8, cape_value: 11.19 },
  { year: 1977, equity_nominal_pct: -6.98, bond_nominal_pct: 1.29, cpi_pct: 6.5, cape_value: 11.44 },
  { year: 1978, equity_nominal_pct: 6.51, bond_nominal_pct: -0.78, cpi_pct: 7.6, cape_value: 9.24 },
  { year: 1979, equity_nominal_pct: 18.52, bond_nominal_pct: 0.67, cpi_pct: 11.3, cape_value: 9.26 },
  { year: 1980, equity_nominal_pct: 31.74, bond_nominal_pct: -2.99, cpi_pct: 13.5, cape_value: 8.85 },
  { year: 1981, equity_nominal_pct: -4.70, bond_nominal_pct: 8.20, cpi_pct: 10.3, cape_value: 9.26 },
  { year: 1982, equity_nominal_pct: 20.42, bond_nominal_pct: 32.81, cpi_pct: 6.2, cape_value: 7.39 },
  { year: 1983, equity_nominal_pct: 22.34, bond_nominal_pct: 3.20, cpi_pct: 3.2, cape_value: 8.76 },
  { year: 1984, equity_nominal_pct: 6.15, bond_nominal_pct: 13.73, cpi_pct: 4.3, cape_value: 9.89 },
  { year: 1985, equity_nominal_pct: 31.24, bond_nominal_pct: 25.71, cpi_pct: 3.6, cape_value: 10.00 },
  { year: 1986, equity_nominal_pct: 18.49, bond_nominal_pct: 24.28, cpi_pct: 1.9, cape_value: 11.72 },
  { year: 1987, equity_nominal_pct: 5.81, bond_nominal_pct: -4.96, cpi_pct: 3.6, cape_value: 14.92 },
  { year: 1988, equity_nominal_pct: 16.54, bond_nominal_pct: 8.22, cpi_pct: 4.1, cape_value: 13.90 },
  { year: 1989, equity_nominal_pct: 31.48, bond_nominal_pct: 17.69, cpi_pct: 4.8, cape_value: 15.09 },
  { year: 1990, equity_nominal_pct: -3.06, bond_nominal_pct: 6.24, cpi_pct: 5.4, cape_value: 17.05 },
  { year: 1991, equity_nominal_pct: 30.23, bond_nominal_pct: 15.00, cpi_pct: 4.2, cape_value: 15.61 },
  { year: 1992, equity_nominal_pct: 7.49, bond_nominal_pct: 9.36, cpi_pct: 3.0, cape_value: 19.77 },
  { year: 1993, equity_nominal_pct: 9.97, bond_nominal_pct: 14.21, cpi_pct: 3.0, cape_value: 20.32 },
  { year: 1994, equity_nominal_pct: 1.33, bond_nominal_pct: -8.04, cpi_pct: 2.6, cape_value: 21.41 },
  { year: 1995, equity_nominal_pct: 37.20, bond_nominal_pct: 23.48, cpi_pct: 2.8, cape_value: 20.22 },
  { year: 1996, equity_nominal_pct: 22.68, bond_nominal_pct: 1.43, cpi_pct: 3.0, cape_value: 24.76 },
  { year: 1997, equity_nominal_pct: 33.10, bond_nominal_pct: 9.94, cpi_pct: 2.3, cape_value: 28.33 },
  { year: 1998, equity_nominal_pct: 28.34, bond_nominal_pct: 14.92, cpi_pct: 1.6, cape_value: 32.86 },
  { year: 1999, equity_nominal_pct: 20.89, bond_nominal_pct: -8.25, cpi_pct: 2.2, cape_value: 40.57 },
  { year: 2000, equity_nominal_pct: -9.03, bond_nominal_pct: 16.66, cpi_pct: 3.4, cape_value: 43.77 },
  { year: 2001, equity_nominal_pct: -11.85, bond_nominal_pct: 5.57, cpi_pct: 2.8, cape_value: 36.98 },
  { year: 2002, equity_nominal_pct: -21.97, bond_nominal_pct: 15.12, cpi_pct: 1.6, cape_value: 30.28 },
  { year: 2003, equity_nominal_pct: 28.36, bond_nominal_pct: 0.38, cpi_pct: 2.3, cape_value: 22.90 },
  { year: 2004, equity_nominal_pct: 10.74, bond_nominal_pct: 4.49, cpi_pct: 2.7, cape_value: 27.66 },
  { year: 2005, equity_nominal_pct: 4.83, bond_nominal_pct: 2.87, cpi_pct: 3.4, cape_value: 26.59 },
  { year: 2006, equity_nominal_pct: 15.61, bond_nominal_pct: 1.96, cpi_pct: 3.2, cape_value: 26.47 },
  { year: 2007, equity_nominal_pct: 5.48, bond_nominal_pct: 10.21, cpi_pct: 2.8, cape_value: 27.21 },
  { year: 2008, equity_nominal_pct: -36.55, bond_nominal_pct: 20.10, cpi_pct: 3.8, cape_value: 24.02 },
  { year: 2009, equity_nominal_pct: 25.94, bond_nominal_pct: -11.12, cpi_pct: -0.4, cape_value: 15.17 },
  { year: 2010, equity_nominal_pct: 14.82, bond_nominal_pct: 8.46, cpi_pct: 1.6, cape_value: 20.53 },
  { year: 2011, equity_nominal_pct: 2.10, bond_nominal_pct: 16.04, cpi_pct: 3.2, cape_value: 22.98 },
  { year: 2012, equity_nominal_pct: 15.89, bond_nominal_pct: 2.97, cpi_pct: 2.1, cape_value: 21.21 },
  { year: 2013, equity_nominal_pct: 32.15, bond_nominal_pct: -9.10, cpi_pct: 1.5, cape_value: 21.90 },
  { year: 2014, equity_nominal_pct: 13.52, bond_nominal_pct: 10.75, cpi_pct: 1.6, cape_value: 24.86 },
  { year: 2015, equity_nominal_pct: 1.38, bond_nominal_pct: 1.28, cpi_pct: 0.1, cape_value: 26.49 },
  { year: 2016, equity_nominal_pct: 11.77, bond_nominal_pct: 0.69, cpi_pct: 1.3, cape_value: 24.21 },
  { year: 2017, equity_nominal_pct: 21.61, bond_nominal_pct: 2.80, cpi_pct: 2.1, cape_value: 28.06 },
  { year: 2018, equity_nominal_pct: -4.23, bond_nominal_pct: -0.02, cpi_pct: 2.4, cape_value: 33.31 },
  { year: 2019, equity_nominal_pct: 31.21, bond_nominal_pct: 9.64, cpi_pct: 1.8, cape_value: 28.38 },
  { year: 2020, equity_nominal_pct: 18.02, bond_nominal_pct: 11.33, cpi_pct: 1.2, cape_value: 30.99 },
  { year: 2021, equity_nominal_pct: 28.47, bond_nominal_pct: -4.42, cpi_pct: 4.7, cape_value: 34.51 },
  { year: 2022, equity_nominal_pct: -18.04, bond_nominal_pct: -17.83, cpi_pct: 8.0, cape_value: 36.94 },
  { year: 2023, equity_nominal_pct: 26.06, bond_nominal_pct: 3.88, cpi_pct: 4.1, cape_value: 28.34 },
  { year: 2024, equity_nominal_pct: 24.88, bond_nominal_pct: -1.64, cpi_pct: 2.9, cape_value: 31.97 },
  { year: 2025, equity_nominal_pct: 17.78, bond_nominal_pct: 7.80, cpi_pct: 2.6, cape_value: 37.14 },
];

export const HISTORICAL_DATA_SOURCE =
  "Damodaran (NYU Stern, histretSP.html, retrieved 2026-05-08); CPI-U annual averages from BLS via usinflationcalculator.com (retrieved 2026-05-08); Shiller CAPE from multpl.com/shiller-pe/table/by-year (retrieved 2026-05-08)";

export const HISTORICAL_FIRST_YEAR = 1928;
export const HISTORICAL_LAST_YEAR = 2025;

/**
 * Returns the inflation-adjusted (real) return series. Computed once at
 * module load via the Fisher equation. Memoized via module-scope const so
 * repeat callers don't re-compute.
 *
 * `((1 + nominal) / (1 + cpi) - 1) × 100`
 */
export const HISTORICAL_REAL_RETURNS: readonly HistoricalRealYear[] =
  HISTORICAL_RETURNS.map((y) => ({
    year: y.year,
    equity_real_pct:
      ((1 + y.equity_nominal_pct / 100) / (1 + y.cpi_pct / 100) - 1) * 100,
    bond_real_pct:
      ((1 + y.bond_nominal_pct / 100) / (1 + y.cpi_pct / 100) - 1) * 100,
  }));

/**
 * CAPE-augmented real-returns view (Phase 6 Session 3). Mirrors
 * `HISTORICAL_REAL_RETURNS` row-for-row but carries the CAPE column,
 * letting the CAPE-based withdrawal strategy walk Trinity windows
 * year-by-year reading the corresponding CAPE for the spend formula.
 */
export const HISTORICAL_REAL_RETURNS_WITH_CAPE: readonly HistoricalRealYearWithCape[] =
  HISTORICAL_RETURNS.map((y) => ({
    year: y.year,
    equity_real_pct:
      ((1 + y.equity_nominal_pct / 100) / (1 + y.cpi_pct / 100) - 1) * 100,
    bond_real_pct:
      ((1 + y.bond_nominal_pct / 100) / (1 + y.cpi_pct / 100) - 1) * 100,
    cape_value: y.cape_value,
  }));

/** Most-recent CAPE in the bundled series — used as the default
 *  forward-projection anchor for the CAPE-based strategy when running
 *  Monte Carlo without a per-year CAPE. */
export const HISTORICAL_LATEST_CAPE: number =
  HISTORICAL_RETURNS[HISTORICAL_RETURNS.length - 1]?.cape_value ?? 30;
